import React, { useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { versionService, Version } from '../services/versionService';
import { useToast } from './ui/Toast';
import { formatDateOnly } from '../utils/formatters';

interface ConfirmDeleteVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  version?: Version | null;
  versions?: Version[];
  mode: 'single' | 'multiple';
}

export const ConfirmDeleteVersionModal: React.FC<ConfirmDeleteVersionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  version,
  versions = [],
  mode
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);

    try {
      if (mode === 'single' && version) {
        await versionService.deleteVersion(version.id);
        toast.success('Versión eliminada exitosamente');
      } else if (mode === 'multiple' && versions.length > 0) {
        const ids = versions.map(v => v.id);
        await versionService.deleteMultipleVersions(ids);
        toast.success(`${versions.length} versiones eliminadas exitosamente`);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting version(s):', error);
      toast.error('Error al eliminar. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const renderSingleDelete = () => {
    if (!version) return null;

    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                Estás a punto de eliminar esta versión
              </h4>
              <p className="text-sm text-red-700">
                Esta acción no se puede deshacer
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">Versión:</span>
            <span className="text-sm font-bold text-slate-900">{version.version_code}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">Tipo:</span>
            <span className="text-sm text-slate-900">{version.tipo}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600">Fecha:</span>
            <span className="text-sm text-slate-900">
              {formatDateOnly(version.fecha)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMultipleDelete = () => {
    if (versions.length === 0) return null;

    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                Estás a punto de eliminar {versions.length} {versions.length === 1 ? 'versión' : 'versiones'}
              </h4>
              <p className="text-sm text-red-700">
                Esta acción no se puede deshacer
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">
            Versiones seleccionadas:
          </h4>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {versions.map((v) => (
              <div
                key={v.id}
                className="bg-white border border-slate-200 rounded px-3 py-2 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-slate-900">{v.version_code}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-600">{v.tipo}</span>
                  <span className="text-xs text-slate-500">
                    {formatDateOnly(v.fecha)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'single' ? 'Confirmar Eliminación' : 'Confirmar Eliminación Múltiple'}
    >
      <div className="space-y-6">
        {mode === 'single' ? renderSingleDelete() : renderMultipleDelete()}

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
            isLoading={loading}
          >
            <Trash2 className="w-4 h-4" />
            Confirmar Eliminación
          </Button>
        </div>
      </div>
    </DialogShell>
  );
};
